import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import axios from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { toastStore } from 'shared/ui'
import { RequestLoadData } from 'shared/api/lib'
import { ManageSeatsUi } from 'entities/Seat/ui/ManageSeats/ui/ManageSeatsUi'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { BillingApi } from 'entities/Billing'
import { PriceInfoStore } from 'entities/Billing/ui/PriceInfo/store/PriceInfoStore'
import { type IResponseErrorBuySeats } from 'entities/Billing/api/types'

export class ManageSeatsStore {
  readonly modalId = nanoid()
  readonly priceInfoStore: PriceInfoStore
  private _disposeSeats: IReactionDisposer | null = null
  private _seats: number | null = 1

  constructor(readonly entities: IEntitiesStore) {
    this.priceInfoStore = new PriceInfoStore((count: number, cancelToken) =>
      BillingApi.getBillingSeatsPreview(
        {
          plan: this.entities.subscriptionStore.plan?.shared_inboxes_plan_id || '',
          seats: count,
          use_unassigned_seats: 0,
        },
        {
          cancelToken: cancelToken,
        }
      )
    )

    makeAutoObservable(this)
  }

  init = () => {
    this.reactionSeats()
    return () => {
      this._disposeSeats?.()
    }
  }

  reactionSeats = () => {
    this._disposeSeats?.()
    this._disposeSeats = reaction(
      () => this._seats,
      (seats) => {
        if (seats) {
          this.priceInfoStore.requestInfoStatus.debounceLoadData(seats)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  openModal = () => {
    modalStore.addModal({
      id: this.modalId,
      title: 'Manage seats',
      pureContent: true,
      ModalContent: () => <ManageSeatsUi store={this} />,
    })
  }

  onCancel = () => {
    modalStore.closeModal(this.modalId)
  }

  get seats() {
    return String(this._seats || '')
  }

  setSeats = (seats: string) => {
    this._seats = seats === '' ? null : Number(seats)
  }

  readonly requestBuySeatsInfoStatus = new RequestLoadData({
    requestCallback: async () => {
      if (!this._seats) {
        return
      }
      const { data } = await BillingApi.buySeats(this._seats)
      this.entities.subscriptionStore.setSeats(data)
      toastStore.add({
        type: 'success',
        title: `${this._seats} seat${this._seats > 1 ? 's' : ''} purchased`,
      })
      this.onCancel()
    },
    errorCallback: (error) => {
      let errorMessage = 'Something went wrong. Please try again later.'

      if (axios.isAxiosError<IResponseErrorBuySeats>(error)) {
        errorMessage =
          error.response?.data.error_message ||
          'Payment failed. Please check your payment method or try again later.'
      }

      this.requestBuySeatsInfoStatus.setError(errorMessage)
    },
  })

  onBuy = async () => {
    return this.requestBuySeatsInfoStatus.loadData()
  }

  get isLoadingInfo() {
    return this.priceInfoStore.requestInfoStatus.loading
  }

  get disabled() {
    return this.isLoadingInfo || !this._seats
  }
}
